import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import ParallaxImage from '../components/motion/ParallaxImage';
import Reveal from '../components/motion/Reveal';
import '../components/motion/motion.css';
import { useNavigate } from 'react-router-dom';
import Container from '../components/ui/Container';
import './AboutPage.css';

const toolingGroups = [
  {
    name: 'AI-assisted development',
    note: 'Agents do the typing; senior engineers own the design and the review.',
    items: ['Claude Code', 'Cursor', 'OpenAI Codex', 'GitHub Copilot', 'Claude Agent SDK', 'MCP servers', 'Playwright'],
  },
  {
    name: 'Models we ship on',
    note: 'Chosen per task class and re-evaluated against our eval sets every quarter.',
    items: ['Claude Opus 5.5', 'Claude Sonnet 5', 'Claude Haiku 4.5', 'GPT-5 family', 'Gemini', 'Llama / Mistral / Qwen on vLLM'],
  },
  {
    name: 'AI application stack',
    note: 'Retrieval, orchestration, tracing and evals on the same Postgres you already run.',
    items: ['LangChain', 'LangGraph', 'LangSmith', 'Langfuse', 'pgvector', 'Braintrust', 'Hugging Face PEFT'],
  },
  {
    name: 'Generative media',
    note: 'Image, video and 3D generation behind a queue, with lineage and cost control.',
    items: ['Higgsfield', 'Seedance', 'Kling', 'Nano Banana', 'Recraft', 'Meshy / Tripo 3D', 'ffmpeg'],
  },
  {
    name: 'Product platform',
    note: 'The reference stack we run in production for every AI product.',
    items: ['Next.js 16', 'FastAPI', 'Django', 'Celery + Redis', 'PostgreSQL', 'async SQLAlchemy 2.0', 'Cloudflare'],
  },
  {
    name: 'Cloud and delivery',
    note: 'Automated from first commit to production, with rollback documented in the repo.',
    items: ['AWS / GCP / Azure', 'Docker / Kubernetes', 'Terraform', 'GitHub Actions', 'OpenTelemetry', 'Grafana'],
  },
];

const AboutPage = () => {
  const navigate = useNavigate();

  const handleContactClick = (e) => {
    e.preventDefault();
    navigate('/contact');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleGoToServices = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('services');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  };

  const handleGoToMethodology = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('methodology');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  };

  // Scroll reveal for zigzag items (toggle on enter/leave for up/down scroll)
  useEffect(() => {
    const zigzagItems = document.querySelectorAll('.zigzag-item');
    if (!('IntersectionObserver' in window) || zigzagItems.length === 0) {
      zigzagItems.forEach((el) => el.classList.add('show'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('show');
          } else {
            entry.target.classList.remove('show');
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );
    zigzagItems.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Scroll reveal for stacked cards (book-like fall and reverse on scroll up)
  useEffect(() => {
    const stackedCards = document.querySelectorAll('.stacked-card');
    if (!('IntersectionObserver' in window) || stackedCards.length === 0) {
      stackedCards.forEach((el) => el.classList.add('show'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('show');
          } else {
            entry.target.classList.remove('show');
          }
        });
      },
      { threshold: 0.25, rootMargin: '0px 0px -10% 0px' }
    );
    stackedCards.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="about-page page-with-header">
      <Helmet>
        <title>About PySolutions — Full-stack AI Engineering Studio</title>
        <meta name="description" content="Serbia-based, senior-led engineering studio building production AI products end to end: backend, frontend, AI/ML, cloud, and DevOps. USA-level expertise with European pricing." />
        <link rel="canonical" href="https://pysolutions.dev/about" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pysolutions.dev/about" />
        <meta property="og:title" content="About PySolutions — Full-stack AI Engineering Studio" />
        <meta property="og:description" content="Senior-led full-stack engineering for production AI products: backend, frontend, AI/ML, cloud, and DevOps shipped end to end." />
        <meta property="og:image" content="https://pysolutions.dev/og-card.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="PySolutions — Full-stack AI Engineering Studio" />
        <meta property="og:site_name" content="PySolutions" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About PySolutions — Full-stack AI Engineering Studio" />
        <meta name="twitter:description" content="Senior-led full-stack engineering for production AI products: backend, frontend, AI/ML, cloud, and DevOps shipped end to end." />
        <meta name="twitter:image" content="https://pysolutions.dev/og-card.jpg" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="about-hero section">
        <div className="about-hero-background">
          <div className="hero-glow hero-glow-1"></div>
          <div className="hero-glow hero-glow-2"></div>
        </div>
        <Container>
          <div className="about-hero-content">
            <div className="hero-badge">
              <span className="badge-icon">💻</span>
              Full-stack AI Engineering
            </div>
            <h1 className="hero-title">
              Give us your hardest engineering problem <span className="logo-highlight">And we'll ship the solution</span>
            </h1>
            <p className="hero-description">
              We combine modern full-stack engineering with AI, data, and pragmatic delivery to ship production-grade products — on time, within scope, with named senior engineers. Every engagement is AI-accelerated: Claude Code and the latest frontier models work in every repo, with senior review and evals on every change.
            </p>
            <div className="cta-actions" style={{ marginTop: '1rem' }}>
              <button onClick={handleContactClick} className="btn-primary cta-primary-btn">
                Book a Free Consultation
                <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                </svg>
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* Company Story / Focus Section */}
      <section className="company-story section">
        <Container>
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">🎯</span>
              Your full-stack engineering partner
            </div>
            <h2 className="section-title">
              From idea to <span className="logo-highlight">production</span>
            </h2>
            <p className="section-subtitle">
              Founded in 2019 in Serbia, our company has grown into a trusted technology partner for clients around the globe. From 2025 we focus on full-stack engineering for production AI products — backends, frontends, AI/ML, cloud, and DevOps shipped end to end for innovative startups and established enterprises.
            </p>
          </div>
          
          <div className="stacked-cards" aria-label="Company story cards">
            <article className="stacked-card" aria-label="Tailored approach" tabIndex={0}>
              <p className="stacked-text">
                Every business is different. We tailor our approach to your constraints—budget, timeline, team capacity—while holding a high bar for quality and reliability.
              </p>
              <div className="cta-actions" style={{ marginTop: '1rem' }}>
                <button onClick={handleGoToMethodology} className="btn-primary cta-primary-btn" aria-label="Go to Our Proven Methodology on the homepage">
                  View Our Methodology
                  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                  </svg>
                </button>
              </div>
            </article>
            <article className="stacked-card" aria-label="Full-stack delivery" tabIndex={0}>
              <p className="stacked-text">
                We design robust backends and modern frontends, AI agents and pipelines, real-time and IoT integrations, data platforms, and the cloud and DevOps that keeps it all running. Python is in our DNA; we use the right stack for each engagement.
              </p>
              <div className="cta-actions" style={{ marginTop: '1rem' }}>
                <button onClick={handleGoToServices} className="btn-primary cta-primary-btn" aria-label="Explore our stack on the homepage">
                  Explore our stack
                  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                  </svg>
                </button>
              </div>
            </article>
            <article className="stacked-card" aria-label="AI‑native MVPs" tabIndex={0}>
              <p className="stacked-text">
                Need a fast, production-ready MVP? We use AI-native delivery to reduce cycle time while keeping reliability front-and-center.
              </p>
              <div className="cta-actions" style={{ marginTop: '1rem' }}>
                <button onClick={handleContactClick} className="btn-primary cta-primary-btn">
                  Start Your MVP
                  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                  </svg>
                </button>
              </div>
            </article>
          </div>
        </Container>
      </section>

      {/* AI‑Native Delivery */}
      <section className="why-choose-us section">
        <Container>
          <div className="section-header">
            <h2 className="section-title">AI‑Native <span className="logo-highlight">Delivery</span></h2>
            <p className="section-subtitle">We build with AI every day: coding agents in the terminal, the right model for each task, and evals on every change.</p>
          </div>
          <ParallaxImage src="/media/about/ai-native.webp" amount={36} />
          <div className="zigzag-list">
            <div className="zigzag-item left">
              <div className="zigzag-card zigzag-orange">
                <h3>AI-accelerated engineering</h3>
                <p>Claude Code, Cursor and background PR agents work in every repo under a written spec and repo conventions. A named senior engineer reviews every change; tests, LLM evals and Playwright checks gate every merge.</p>
              </div>
            </div>
            <div className="zigzag-item right">
              <div className="zigzag-card zigzag-blue">
                <h3>The right model for each task</h3>
                <p>Claude Opus 5.5 for architecture and hard refactors, Claude Sonnet 5 and Haiku 4.5 for everyday and high-volume work, the GPT-5 family or Gemini where you already standardise on them, and open-weights models on vLLM when data must stay in-region.</p>
              </div>
            </div>
            <div className="zigzag-item left">
              <div className="zigzag-card zigzag-green">
                <h3>AI in the product, not just the process</h3>
                <p>RAG, agents and eval harnesses, MCP servers that make your systems agent-ready, and generative media pipelines on Higgsfield-class model APIs (Seedance, Kling, Nano Banana, image-to-3D) for product and marketing visuals. Weekly demos on a real preview URL.</p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Tooling we build with */}
      <section className="tooling-section section">
        <Container>
          <Reveal className="section-header">
            <h2 className="section-title">What we build <span className="logo-highlight">with</span></h2>
            <p className="section-subtitle">The tools and models in daily use on client work, updated as the landscape moves.</p>
          </Reveal>
          <div className="tooling-grid">
            {toolingGroups.map((group, index) => (
              <Reveal key={group.name} as="article" className="tooling-group" delay={(index % 3) * 0.08} y={20}>
                <h3 className="tooling-group-title">{group.name}</h3>
                <p className="tooling-group-note">{group.note}</p>
                <ul className="tooling-list" aria-label={group.name}>
                  {group.items.map((item) => (
                    <li key={item} className="tooling-chip">{item}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Modernization & Digital Core */}
      <section className="why-choose-us section">
        <Container>
          <div className="section-header">
            <h2 className="section-title">Modernization & <span className="logo-highlight">Digital Core</span></h2>
            <p className="section-subtitle">Lay the groundwork to leverage advanced technologies safely and at scale.</p>
          </div>
          <ParallaxImage src="/media/about/modernization.webp" amount={36} />
          <div className="zigzag-list">
            <div className="zigzag-item right">
              <div className="zigzag-card zigzag-purple">
                <h3>Legacy → Modern Stack</h3>
                <p>Migrate monoliths and scripts to modular services, modern frontends, and maintainable architectures — without breaking the team.</p>
              </div>
            </div>
            <div className="zigzag-item left">
              <div className="zigzag-card zigzag-teal">
                <h3>Unified Data Foundation</h3>
                <p>Design data models, pipelines, and APIs that make AI and analytics practical and reliable.</p>
              </div>
            </div>
            <div className="zigzag-item right">
              <div className="zigzag-card zigzag-red">
                <h3>Cloud‑Ready & Secure</h3>
                <p>Deploy with best practices for security, observability, resilience, and cost control.</p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Trusted Partnership */}
      <section className="why-choose-us section">
        <Container>
          <div className="section-header">
            <h2 className="section-title">A Trusted <span className="logo-highlight">Partner</span></h2>
            <p className="section-subtitle">Collaboration designed to augment intelligence across your organization.</p>
          </div>
          <ParallaxImage src="/media/about/partner.webp" amount={36} />
          <div className="zigzag-list">
            <div className="zigzag-item left">
              <div className="zigzag-card zigzag-blue">
                <h3>Multidisciplinary Engineering</h3>
                <p>Backend, data, AI, GUIs, and IoT—integrated by a small, senior team with domain awareness.</p>
              </div>
            </div>
            <div className="zigzag-item right">
              <div className="zigzag-card zigzag-orange">
                <h3>Transparent Collaboration</h3>
                <p>NDA-friendly, weekly demos, and written status so stakeholders stay aligned.</p>
              </div>
            </div>
            <div className="zigzag-item left">
              <div className="zigzag-card zigzag-purple">
                <h3>Flexible Engagement</h3>
                <p>Fixed-scope sprints, T&amp;M, or retainers—whichever best fits your goals and risk profile.</p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section section">
        <div className="cta-background">
          <div className="cta-glow cta-glow-1"></div>
          <div className="cta-glow cta-glow-2"></div>
        </div>
        <div className="cta-content">
          <h3>Create real value with <span className="logo-highlight">leading capabilities</span></h3>
          <p>Whether you’re launching a new product or shaping your AI strategy, we’ll help you meet evolving market needs.</p>
          <div className="cta-actions">
            <button onClick={handleContactClick} className="btn-primary cta-primary-btn">
              Get a Proposal in 48 Hours
              <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
              </svg>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage; 